// hooks/useSubscription.ts
import { useState, useEffect } from 'react';
import { 
  getSubscriptionData, 
  checkEligibility, 
  createPaymentSession, 
  modifySubscriptionAPI,
  cancelSubscriptionAPI,
  getSubscriptionBilling,
  getTransactionDetails
} from '@/services/dashboardService';
import { SubscriptionPlan, SubscriptionDetails, EligibilityResponse, PaymentRequest } from '../types';
import toast from 'react-hot-toast';

export const planHierarchy = ['spark', 'grow', 'thrive', 'pinnacle'] as const;

export function useSubscription() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [eligibilityData, setEligibilityData] = useState<EligibilityResponse | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<{ data: SubscriptionPlan[] } | null>(null);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null)
  const [currentPlan, setCurrentPlan] = useState<any>(null)
  const [billing, setBilling] = useState<any>(null)
  const [transactions, setTransactions] = useState<any[]>([])

  const fetchSubscriptionData = async () => {
    setIsLoading(true);
    try {
      const res = await getSubscriptionData();
      if(res?.currentPlan) {
        setCurrentPlan(res?.currentPlan)
      }
      if (res?.data) {
        const plansArray = Object.values(res.data).map((plan: any) => ({
          name: plan.name,
          details: plan.details
        })) as SubscriptionPlan[];
        setSubscriptionData({ data: plansArray });
        setSelectedPlan(currentPlan?.planType || '');
        setIsAnnual(currentPlan?.paymentFrequency==="annually")
      } else {
        setSubscriptionData({ data: [] });
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setSubscriptionData({ data: [] });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
    fetchBilling();
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan?.planType, currentPlan?.paymentFrequency]);

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
  };

  const isDowngrade = (currentPlan: string, selectedPlan: string) => {
    const currentIndex = planHierarchy.indexOf(currentPlan as any);
    const selectedIndex = planHierarchy.indexOf(selectedPlan as any);
    return currentIndex > selectedIndex;
  };

  const handleSubmit = async (e: React.FormEvent, plan: any) => {
    e.preventDefault();
    setIsLoading(true)
    const subscriptionDetails: SubscriptionDetails = {
      planType: plan,
      paymentFrequency: isAnnual ? 'annually' : 'monthly'
    };

    try {
      const eligibility = await checkEligibility(subscriptionDetails);
      if(eligibility?.success) {
        setPreviewData(eligibility.data)
        setIsLoading(false)
        return eligibility
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPaymentUrl = async (planReq: PaymentRequest) => {
    try {
      const response = await createPaymentSession(planReq) 
      if(response.success) {
       return response.data
      }
      return {}
    } catch (error) {
      toast.error('An unexpected error occurred')
    }
  }

  const modifySubscription = async (planReq: PaymentRequest) => {
    try {
      const response = await modifySubscriptionAPI(planReq) 
      return response
    } catch (error) {
      toast.error('An unexpected error occurred')
    }
  }

  const cancelSubscription = async () => {
    try {
      const response = await cancelSubscriptionAPI() 
      return response
    } catch (error) {
      toast.error('An unexpected error occurred')
    }
  }

  const fetchBilling = async () => {
    try {
      const response = await getSubscriptionBilling()
      if (response.success) {        
        setBilling(response.data.data || null)
      } else {
        // toast({
        //   title: 'Error',
        //   description: response.error,
        //   variant: 'destructive'
        // })
        toast.success(response?.error || 'Failed to fetch billing info')
      }
    } catch (error) {
      // Billing fetch error is non-critical, so we just log it
      console.error('Failed to fetch billing info:', error)
    }
  }

  const fetchTransactions = async () => {
    try {
      const response = await getTransactionDetails()
      if (response.success) {        
        setTransactions(response.data || null)
      } else {
        // toast({
        //   title: 'Error',
        //   description: response.error,
        //   variant: 'destructive'
        // })
        toast.success(response?.error || 'Failed to fetch transactions data')
      }
    } catch (error) {
      // Billing fetch error is non-critical, so we just log it
      console.error('Failed to fetch billing info:', error)
    }
  }

  return {
    isAnnual,
    setIsAnnual,
    selectedPlan,
    isLoading,
    isSubmitting,
    showModal,
    setShowModal,
    eligibilityData,
    subscriptionData,
    eligibilityError,
    handlePlanSelect,
    handleSubmit,
    setEligibilityData,
    setEligibilityError,
    isDowngrade,
    setSelectedPlan,
    previewData,
    createPaymentSession,
    getPaymentUrl,
    modifySubscription,
    currentPlan,
    cancelSubscription,
    billing,
    fetchSubscriptionData,
    fetchTransactions,
    transactions
  };
}